export type RoleKey =
  | 'distributor'
  | 'subDistributor'
  | 'agent'
  | 'shop'
  | 'player';

export type GraphQLRoleName =
  | 'DISTRIBUTOR'
  | 'SUBDISTRIBUTOR'
  | 'AGENT'
  | 'SHOP'
  | 'USER';

export type AuthRoleName =
  | 'ADMIN'
  | 'DISTRIBUTOR'
  | 'SUBDISTRIBUTOR'
  | 'AGENT'
  | 'SHOP';

export interface RoleConfig {
  key: RoleKey;
  graphqlRoleName: GraphQLRoleName;

  // UI text
  label: string;
  pageTitle: string;
  nameColumnLabel: string;
  searchByLabel: string;
  addButtonLabel: string;
  loadingText: string;
  emptyStateMessage: string;
  successHeading: string;

  // routing
  routeBase: string;        // e.g. /distributor — used for list + success
  createRoute: string;      // e.g. /distributor/create — sometimes differs (SubDistributor uses /subdistributor/create)

  // child role (used for navigating into a row's user detail; null when leaf)
  childRoleKey: RoleKey | null;

  // list page feature flags
  showWagerCashbackToggles: boolean;
  showOnlinePaymentToggle: boolean;
  showPlayingGameColumn: boolean;
  balanceIncludesOnHold: boolean;
  searchMode: 'enter' | 'debounce-keyup';

  // create page: which authenticated-user roles can pick a parent for this role
  // (when current user IS one tier above, their id is auto-used and dropdown is hidden)
  parentSelectorRoles: AuthRoleName[];
  // role of the parent dropdown items (e.g., for Distributor it's 'ADMIN', for Player it's 'SHOP')
  parentDropdownRoleName: GraphQLRoleName | 'ADMIN';
  parentDropdownLabel: string;
  // when current user has this role, parentId is auto-set to currentUser.id (no dropdown)
  autoAssignParentForRole: AuthRoleName | null;

  // success page
  successPageMode: 'static-qr' | 'dynamic-qr';
  successShowsName: boolean;
  successShowsPhone: boolean;
}

export const ROLE_CONFIGS: Record<RoleKey, RoleConfig> = {
  distributor: {
    key: 'distributor',
    graphqlRoleName: 'DISTRIBUTOR',
    label: 'Distributor',
    pageTitle: 'Distributor Page',
    nameColumnLabel: 'Distributer Name',
    searchByLabel: 'Search by Distributer Name',
    addButtonLabel: 'Add New Distributor',
    loadingText: 'Loading distributors...',
    emptyStateMessage: 'No distributor data found.',
    successHeading: 'Distributor Created Succesfully',
    routeBase: '/distributor',
    createRoute: '/distributor/create',
    childRoleKey: 'subDistributor',
    showWagerCashbackToggles: true,
    showOnlinePaymentToggle: true,
    showPlayingGameColumn: false,
    balanceIncludesOnHold: false,
    searchMode: 'enter',
    parentSelectorRoles: ['ADMIN'],
    parentDropdownRoleName: 'ADMIN',
    parentDropdownLabel: 'Assign to Admin',
    autoAssignParentForRole: null,
    successPageMode: 'static-qr',
    successShowsName: false,
    successShowsPhone: false,
  },
  subDistributor: {
    key: 'subDistributor',
    graphqlRoleName: 'SUBDISTRIBUTOR',
    label: 'SubDistributor',
    pageTitle: 'SubDistributor Page',
    nameColumnLabel: 'SubDistributor Name',
    searchByLabel: 'Search by SubDistributor Name',
    addButtonLabel: 'Add New SubDistributor',
    loadingText: 'Loading sub-distributors...',
    emptyStateMessage: 'No sub-distributor data found.',
    successHeading: 'SubDistributor Created Succesfully',
    routeBase: '/sub-distributor',
    createRoute: '/subdistributor/create',
    childRoleKey: 'agent',
    showWagerCashbackToggles: true,
    showOnlinePaymentToggle: true,
    showPlayingGameColumn: false,
    balanceIncludesOnHold: false,
    searchMode: 'enter',
    parentSelectorRoles: ['ADMIN'],
    parentDropdownRoleName: 'DISTRIBUTOR',
    parentDropdownLabel: 'Assign to Distributor',
    autoAssignParentForRole: 'DISTRIBUTOR',
    successPageMode: 'static-qr',
    successShowsName: false,
    successShowsPhone: false,
  },
  agent: {
    key: 'agent',
    graphqlRoleName: 'AGENT',
    label: 'Agent',
    pageTitle: 'Agent Page',
    nameColumnLabel: 'Agent Name',
    searchByLabel: 'Search by Agent Name',
    addButtonLabel: 'Add New Agent',
    loadingText: 'Loading agents...',
    emptyStateMessage: 'No agent data found.',
    successHeading: 'Agent Created Succesfully',
    routeBase: '/agent',
    createRoute: '/agent/create',
    childRoleKey: 'shop',
    showWagerCashbackToggles: true,
    showOnlinePaymentToggle: true,
    showPlayingGameColumn: false,
    balanceIncludesOnHold: false,
    searchMode: 'enter',
    parentSelectorRoles: ['ADMIN', 'DISTRIBUTOR'],
    parentDropdownRoleName: 'SUBDISTRIBUTOR',
    parentDropdownLabel: 'Assign to SubDistributor',
    autoAssignParentForRole: 'SUBDISTRIBUTOR',
    successPageMode: 'static-qr',
    successShowsName: false,
    successShowsPhone: false,
  },
  shop: {
    key: 'shop',
    graphqlRoleName: 'SHOP',
    label: 'Shop',
    pageTitle: 'Shop Page',
    nameColumnLabel: 'Shop Name',
    searchByLabel: 'Search by Shop Name',
    addButtonLabel: 'Add New Shop',
    loadingText: 'Loading shops...',
    emptyStateMessage: 'No shop data found.',
    successHeading: 'Shop Created Succesfully',
    routeBase: '/shop',
    createRoute: '/shop/create',
    childRoleKey: 'player',
    showWagerCashbackToggles: true,
    showOnlinePaymentToggle: true,
    showPlayingGameColumn: false,
    balanceIncludesOnHold: false,
    searchMode: 'enter',
    parentSelectorRoles: ['ADMIN', 'DISTRIBUTOR', 'SUBDISTRIBUTOR'],
    parentDropdownRoleName: 'AGENT',
    parentDropdownLabel: 'Assign to Agent',
    autoAssignParentForRole: 'AGENT',
    successPageMode: 'static-qr',
    successShowsName: false,
    successShowsPhone: false,
  },
  player: {
    key: 'player',
    graphqlRoleName: 'USER',
    label: 'Player',
    pageTitle: 'Player Page',
    nameColumnLabel: 'Player Name',
    searchByLabel: 'Search by Player Name',
    addButtonLabel: 'Add New Player',
    loadingText: 'Loading players...',
    emptyStateMessage: 'No player data found.',
    successHeading: 'Player Created Successfully',
    routeBase: '/player',
    createRoute: '/player/create',
    childRoleKey: null,
    showWagerCashbackToggles: false,
    showOnlinePaymentToggle: false,
    showPlayingGameColumn: true,
    balanceIncludesOnHold: true,
    searchMode: 'debounce-keyup',
    parentSelectorRoles: ['ADMIN', 'DISTRIBUTOR', 'SUBDISTRIBUTOR', 'AGENT'],
    parentDropdownRoleName: 'SHOP',
    parentDropdownLabel: 'Assign to Shop',
    autoAssignParentForRole: 'SHOP',
    successPageMode: 'dynamic-qr',
    successShowsName: true,
    successShowsPhone: true,
  },
};
